import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Plus, Trash2, Workflow } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import api from '../services/api';

const Stages = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const [stageForm, setStageForm] = useState({
    stageName: '',
    stageCode: '',
    displayOrder: '',
    description: '',
  });

  useEffect(() => {
    loadStages();
  }, []);

  const loadStages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/stages');
      setStages(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load stages',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStage = async () => {
    if (!stageForm.stageName || !stageForm.stageCode || !stageForm.displayOrder) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      await api.post('/stages', {
        stage_name: stageForm.stageName,
        stage_code: stageForm.stageCode.toLowerCase().replace(/\s+/g, '_'),
        display_order: parseInt(stageForm.displayOrder),
        description: stageForm.description,
      });

      toast({
        title: 'Success',
        description: `Stage "${stageForm.stageName}" created successfully`,
      });

      setStageForm({
        stageName: '',
        stageCode: '',
        displayOrder: '',
        description: '',
      });
      setIsCreateDialogOpen(false);
      loadStages();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create stage',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteStage = async (stageId, stageName) => {
    if (!window.confirm(`Are you sure you want to delete stage "${stageName}"? This may affect existing orders.`)) {
      return;
    }

    try {
      await api.delete(`/stages/${stageId}`);
      toast({
        title: 'Success',
        description: 'Stage deleted successfully',
      });
      loadStages();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete stage',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 shadow-md">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-3 min-w-0">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-emerald-700 p-2 flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold truncate">Production Stages</h1>
              <p className="text-xs text-emerald-100">Manage workflow stages</p>
            </div>
          </div>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="bg-white text-emerald-700 hover:bg-emerald-50 text-sm h-9 px-3 flex-shrink-0"
          >
            <Plus className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Add Stage</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading stages...</p>
            </div>
          ) : stages.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Workflow className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No stages configured yet</p>
              <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-emerald-500 hover:bg-emerald-600">
                <Plus className="w-4 h-4 mr-2" />
                Add First Stage
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {stages.map((stage) => (
                <div
                  key={stage.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center font-bold text-purple-600">
                        {stage.display_order}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 text-lg">{stage.stage_name}</h3>
                        <p className="text-sm text-gray-600 font-mono">Code: {stage.stage_code}</p>
                        {stage.description && (
                          <p className="text-xs text-gray-500 mt-1">{stage.description}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteStage(stage.id, stage.stage_name)}
                      className="border-red-500 text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Stage Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Add New Production Stage</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="stageName" className="text-base">Stage Name *</Label>
              <Input
                id="stageName"
                value={stageForm.stageName}
                onChange={(e) => setStageForm({ ...stageForm, stageName: e.target.value })}
                placeholder="e.g., Casting, Polishing, Stone Setting"
                className="text-base py-6"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="stageCode" className="text-base">Stage Code *</Label>
              <Input
                id="stageCode"
                value={stageForm.stageCode}
                onChange={(e) => setStageForm({ ...stageForm, stageCode: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                placeholder="e.g., casting, polishing"
                className="text-base py-6 font-mono"
              />
              <p className="text-xs text-gray-500">Unique identifier (auto-formatted to lowercase with underscores)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayOrder" className="text-base">Display Order *</Label>
              <Input
                id="displayOrder"
                type="number"
                inputMode="numeric"
                value={stageForm.displayOrder}
                onChange={(e) => setStageForm({ ...stageForm, displayOrder: e.target.value })}
                placeholder="1, 2, 3..."
                className="text-base py-6"
              />
              <p className="text-xs text-gray-500">Order in the production workflow (1 = first stage)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-base">Description (Optional)</Label>
              <textarea
                id="description"
                value={stageForm.description}
                onChange={(e) => setStageForm({ ...stageForm, description: e.target.value })}
                placeholder="Brief description of this stage..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Preview:</strong> Stage will appear as "{stageForm.stageName || 'Stage Name'}" in the workflow
              </p>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)} className="text-base py-6">
              Cancel
            </Button>
            <Button onClick={handleCreateStage} className="bg-emerald-500 hover:bg-emerald-600 text-base py-6">
              Add Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Stages;
